import { useEffect, useRef, useState } from 'react';
import LangLink from '../../../../components/common/LangLink';
import { useTheme } from '../../../../context/ThemeContext';

const AuthoritySection = ({ language }) => {
    const fr = language === 'fr';
    const lang = fr ? 'fr' : 'en';
    const { theme } = useTheme();
    const themeStr = theme === 'dark' ? 'dark' : 'light';

    const content = {
        kicker: fr ? 'FORMATION APPROUVÉE' : 'APPROVED TRAINING',
        titleDim: fr ? 'Développée pour ' : 'Developed for ',
        titleBright: fr ? 'Transports Canada' : 'Transport Canada',
        ctaLabel: fr ? 'Nos abonnements' : 'Our subscriptions',
        blocks: [
            {
                key: 'intro',
                icon: null,
                title: null,
                body: fr
                    ? 'Contenus à jour, conformes aux exigences de Transports Canada, pour t’entraîner sur des bases fiables. Tout est structuré pour t’aider à maîtriser les concepts, savoir exactement quoi travailler et avancer avec méthode. Une seule plateforme, tout ce dont tu as besoin.'
                    : 'Up-to-date content built to Transport Canada standards, so you’re studying from a source you can trust. Everything is structured to help you master the concepts, know exactly what to work on, and move forward with a plan. One platform, everything you need.',
                image: `/img/landing/authority_intro_${lang}_${themeStr}.webp`,
            },
            {
                key: 'certified',
                icon: '/img/landing/authority_icon_certified.svg',
                title: fr ? 'Cours certifié' : 'Certified courses',
                body: fr
                    ? 'Construits par des instructeurs qualifiés, nos cours suivent à la lettre le programme officiel de Transports Canada. Chapitre par chapitre, objectif par objectif, tu étudies exactement ce qui tombe à l’examen, sans rien laisser au hasard.'
                    : 'Our courses follow the official Transport Canada syllabus to the letter, built and reviewed by certified instructors. Chapter by chapter, learning objective by learning objective, you study exactly what the exam covers, with nothing left to chance.',
                image: `/img/landing/authority_certified_${lang}_${themeStr}.webp`,
            },
            {
                key: 'letter',
                icon: '/img/landing/authority_icon_letter.svg',
                title: fr ? 'Lettre de recommandation officielle' : 'Official recommendation letter',
                body: fr
                    ? 'À l’issue de ta formation, obtiens ta lettre de recommandation officielle pour l’examen de Transports Canada.'
                    : 'When your training’s done, you get your official letter of recommendation for the Transport Canada exam.',
                image: `/img/landing/authority_letter_${lang}_${themeStr}.webp`,
            },
        ],
    };

    // === DESKTOP: Cardless pattern — image LEFT, text RIGHT (alternance) ===
    const [activeIndex, setActiveIndex] = useState(0);
    const blockRefs = useRef([]);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        const idx = blockRefs.current.indexOf(entry.target);
                        if (idx !== -1) setActiveIndex(idx);
                    }
                });
            },
            { rootMargin: '-45% 0px -45% 0px', threshold: 0 }
        );
        blockRefs.current.forEach((ref) => ref && observer.observe(ref));
        return () => observer.disconnect();
    }, []);

    // === MOBILE: horizontal carousel (intro + 2 sub-cards) ===
    const [mobileActive, setMobileActive] = useState(0);
    const mobileScrollRef = useRef(null);
    const mobileCardRefs = useRef([]);

    useEffect(() => {
        const root = mobileScrollRef.current;
        if (!root) return;
        const observer = new IntersectionObserver(
            (entries) => {
                let best = null;
                entries.forEach((entry) => {
                    if (!best || entry.intersectionRatio > best.intersectionRatio) best = entry;
                });
                if (best && best.isIntersecting) {
                    const idx = mobileCardRefs.current.indexOf(best.target);
                    if (idx !== -1) setMobileActive(idx);
                }
            },
            { root, threshold: [0.5, 0.75, 1] }
        );
        mobileCardRefs.current.forEach((ref) => ref && observer.observe(ref));
        return () => observer.disconnect();
    }, []);

    const scrollToMobileCard = (idx) => {
        const card = mobileCardRefs.current[idx];
        if (card) card.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    };

    const renderHeader = (variant) => (
        <div>
            <p
                className={`font-['Rethink_Sans',sans-serif] font-bold text-[#71a7ff] ${
                    variant === 'mobile' ? 'text-[13px] tracking-[3px] leading-[24px]' : 'text-[14px] tracking-[3.5px] leading-[34px]'
                }`}
            >
                {content.kicker}
            </p>
            <h2
                className={`font-['Rethink_Sans',sans-serif] font-normal mt-3 ${
                    variant === 'mobile'
                        ? 'text-[40px] leading-[40px] tracking-[-1px]'
                        : 'text-[50px] leading-[46px] tracking-[-1.5px] mt-4'
                }`}
            >
                <span className="text-gray-400 dark:text-white/40">{content.titleDim}</span>
                <span className="text-gray-900 dark:text-white">{content.titleBright}</span>
            </h2>
        </div>
    );

    const renderCTA = () => (
        <LangLink
            to="/levels"
            className="inline-flex items-center justify-center bg-[#0061fe] text-white font-['Manrope',sans-serif] font-semibold text-[13px] px-[26px] py-[18px] rounded-full hover:bg-[#0052db] active:scale-[0.98] transition-all shadow-lg shadow-[#0061fe]/20"
        >
            {content.ctaLabel}
        </LangLink>
    );

    return (
        <section className="relative bg-white dark:bg-[#0f0f0f] text-gray-900 dark:text-white">
            {/* ================= MOBILE: horizontal carousel ================= */}
            <div className="md:hidden max-w-[1151px] mx-auto px-6 py-16">
                <div className="mb-10">{renderHeader('mobile')}</div>

                <div className="-mx-6">
                    <div
                        ref={mobileScrollRef}
                        className="flex overflow-x-auto snap-x snap-mandatory gap-4 px-6 pb-4 no-scrollbar"
                    >
                        {content.blocks.map((block, i) => (
                            <div
                                key={block.key}
                                ref={(el) => (mobileCardRefs.current[i] = el)}
                                className="snap-center shrink-0 w-[82%] max-w-[340px] flex flex-col gap-5"
                            >
                                <div className="aspect-[505/587] w-full rounded-[20px] overflow-hidden bg-gray-100 dark:bg-white/5">
                                    <img
                                        src={block.image}
                                        alt={block.title || ''}
                                        className="w-full h-full object-cover"
                                        loading="lazy"
                                    />
                                </div>
                                {block.icon && <img src={block.icon} alt="" className="w-[52px] h-[52px]" />}
                                {block.title && (
                                    <h3 className="font-['Rethink_Sans',sans-serif] font-semibold text-[20px] leading-[26px] text-gray-900 dark:text-white">
                                        {block.title}
                                    </h3>
                                )}
                                <p className="font-['Rethink_Sans',sans-serif] font-normal text-[15px] leading-[24px] text-gray-600 dark:text-[#a9b1bd]">
                                    {block.body}
                                </p>
                            </div>
                        ))}
                    </div>

                    <div className="flex justify-center gap-2 mt-4">
                        {content.blocks.map((block, i) => (
                            <button
                                key={block.key}
                                type="button"
                                aria-label={`Go to slide ${i + 1}`}
                                onClick={() => scrollToMobileCard(i)}
                                className={`transition-all duration-300 rounded-full ${
                                    i === mobileActive ? 'w-6 h-2 bg-gray-900 dark:bg-white' : 'w-2 h-2 bg-gray-300 dark:bg-white/30 hover:bg-gray-400 dark:hover:bg-gray-100 dark:bg-white/50'
                                }`}
                            />
                        ))}
                    </div>
                </div>

                <div className="mt-10 flex justify-center">{renderCTA()}</div>
            </div>

            {/* ================= DESKTOP: Cardless pattern — IMAGE LEFT, TEXT RIGHT ================= */}
            <div className="hidden md:block max-w-[1151px] mx-auto px-6 py-24">
                <div className="grid grid-cols-[505px_521px] gap-[125px]">
                    {/* LEFT: sticky image stack with crossfade */}
                    <div>
                        <div style={{ position: 'sticky', top: 'calc(50vh - 293px)' }}>
                            <div className="relative w-[505px] h-[587px] rounded-[20px] overflow-hidden">
                                {content.blocks.map((block, i) => (
                                    <img
                                        key={block.key}
                                        src={block.image}
                                        alt={block.title || ''}
                                        className="absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ease-out"
                                        style={{ opacity: i === activeIndex ? 1 : 0 }}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* RIGHT: header + 3 blocks (intro w/o icon, then 2 sub-cards) + CTA */}
                    <div>
                        <div>{renderHeader('desktop')}</div>

                        <div className="flex flex-col">
                            {content.blocks.map((block, i) => (
                                <div
                                    key={block.key}
                                    ref={(el) => (blockRefs.current[i] = el)}
                                    className={`flex flex-col gap-[27px] py-[140px] ${
                                        i > 0 ? 'border-t border-gray-200 dark:border-white/10' : ''
                                    }`}
                                >
                                    {block.icon && <img src={block.icon} alt="" className="w-[62px] h-[62px]" />}
                                    {block.title && (
                                        <h3 className="font-['Rethink_Sans',sans-serif] font-semibold text-[25px] leading-[31px] text-gray-900 dark:text-white">
                                            {block.title}
                                        </h3>
                                    )}
                                    <p className="font-['Rethink_Sans',sans-serif] font-normal text-[20px] leading-[31px] text-gray-600 dark:text-[#a9b1bd]">
                                        {block.body}
                                    </p>
                                </div>
                            ))}
                        </div>

                        <div className="mt-12">{renderCTA()}</div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default AuthoritySection;
